import { useRef, useEffect, useState } from 'react';
import {
  approvedTeamChoicesSchema,
  type SessionContext,
  type ApprovedTeamChoices,
} from '@nerds-at-play/contracts';
import { entryApi, EntryError, entryMessage } from './formation-api.js';
export function TeamLoginPanel({
  initialName = '',
  onSession,
}: {
  initialName?: string;
  onSession: (value: SessionContext) => void;
}) {
  const [name, setName] = useState(initialName);
  const [choices, setChoices] = useState<ApprovedTeamChoices>({ teams: [] });
  const [listError, setListError] = useState('');
  const [loading, setLoading] = useState(true);
  async function loadTeams(cursor?: string) {
    setLoading(true);
    setListError('');
    try {
      const raw = await entryApi<unknown>(
        `/api/public/teams${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      );
      const parsed = approvedTeamChoicesSchema.safeParse(raw);
      if (!parsed.success) throw new Error('This preview API needs the latest team-list update.');
      const result = parsed.data;
      if (alive.current)
        setChoices((previous) => ({
          ...result,
          teams: cursor ? [...previous.teams, ...result.teams] : result.teams,
        }));
    } catch (e) {
      if (alive.current) setListError(entryMessage(e));
    } finally {
      if (alive.current) setLoading(false);
    }
  }
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    void loadTeams();
    return () => {
      alive.current = false;
    };
  }, []);
  async function login(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await entryApi<SessionContext>('/api/session/team', 'POST', { name, pin });
      if (alive.current) onSession(result);
    } catch (e) {
      if (alive.current)
        setError(
          e instanceof EntryError && e.status === 401
            ? 'Unable to join with that name and PIN. Check both, check request status, or ask the Game Master in person.'
            : entryMessage(e),
        );
    } finally {
      if (alive.current) {
        setPin('');
        setBusy(false);
      }
    }
  }
  return (
    <section aria-label="Approved team login">
      <p>
        Choose your approved team and enter its six-digit shared PIN. Each teammate can join on
        their own device.
      </p>
      <div aria-label="Approved teams">
        {choices.teams.map((team) => (
          <button
            type="button"
            key={team.name}
            aria-pressed={name === team.name}
            disabled={busy}
            onClick={() => {
              setName(team.name);
              setPin('');
              setError('');
            }}
          >
            {team.name}
          </button>
        ))}
        {loading && <p role="status">Loading approved teams…</p>}
        {listError && (
          <p role="alert" className="entry-error">
            Could not load approved teams. {listError}
          </p>
        )}
        {!loading && !listError && choices.teams.length === 0 && (
          <p>No approved teams listed yet. Check request status or ask the Game Master.</p>
        )}
        {choices.cursor && (
          <button
            type="button"
            disabled={loading || busy}
            onClick={() => void loadTeams(choices.cursor)}
          >
            More teams
          </button>
        )}
        <button type="button" disabled={loading || busy} onClick={() => void loadTeams()}>
          Refresh teams
        </button>
      </div>
      {error && (
        <p role="alert" className="entry-error">
          {error}
        </p>
      )}
      <form onSubmit={(event) => void login(event)}>
        <label>
          Team entry name
          <input
            required
            maxLength={80}
            autoComplete="username"
            value={name}
            disabled={busy}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label>
          Shared PIN
          <input
            required
            type="password"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="current-password"
            value={pin}
            disabled={busy}
            onChange={(event) => setPin(event.target.value)}
          />
        </label>
        <p className="entry-detail">
          Forgotten PIN? Ask the Game Master to check your team in person and set a replacement.
          Everyone on your team will need to sign in again.
        </p>
        <button className="board-primary" disabled={busy}>
          {busy ? 'Joining…' : 'Join approved team'}
        </button>
      </form>
    </section>
  );
}
